import React, { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import authService from './services/authService';
import './LoginPage.css';

const LoginPage = ({ onLoginSuccess }) => {
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDevHelpers, setShowDevHelpers] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        const status = await authService.checkAuthStatus();
        if (status.authenticated) {
          onLoginSuccess(status.user);
        }
      }
    };
    checkAuth();
  }, [onLoginSuccess]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await login(credentials.username, credentials.password);
      onLoginSuccess(response.user);
    } catch (error) {
      setError(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTestUser = async () => {
    try {
      const testUserData = {
        username: 'testuser',
        email: 'test@example.com',
        password: 'testpass123',
        first_name: 'Test',
        last_name: 'User'
      };
      
      await authService.createTestUser(testUserData);
      setCredentials({
        username: 'testuser',
        password: 'testpass123'
      });
      alert('Test user created! Username: testuser, Password: testpass123');
    } catch (error) {
      if (error.message.includes('Username already exists')) {
        setCredentials({
          username: 'testuser',
          password: 'testpass123'
        });
        alert('Test user already exists. Username: testuser, Password: testpass123');
      } else {
        alert('Failed to create test user: ' + error.message);
      }
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <img 
              src="/anti-virus-shield.png" 
              alt="ZGPT Logo" 
              className="logo"
            />
            <h1>VILLA Evaluation</h1>
          </div>
          <p className="subtitle">
            Versatile Information retrieval from scientific Literature using large LAnguage models
          </p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <h2>Researcher Login</h2>
          
          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter your username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter your password"
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading || !credentials.username || !credentials.password}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            <strong>For Research Participants:</strong><br/>
            Your account credentials will be provided by the research team.
            Please contact the administrators if you need access.
          </p>
          
          {/* Development helpers - only show in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="dev-helpers">
              <button 
                onClick={() => setShowDevHelpers(!showDevHelpers)}
                className="dev-toggle"
              >
                {showDevHelpers ? 'Hide' : 'Show'} Dev Tools
              </button>
              
              {showDevHelpers && (
                <div className="dev-tools">
                  <h3>Development Tools</h3>
                  <button onClick={handleCreateTestUser} className="dev-button">
                    Create Test User
                  </button>
                  <p className="dev-note">
                    This will create a test account: testuser / testpass123
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
